/**
 * Resume extras — everything generated FROM the client's saved resume:
 *   - French version (AI translation, then fully editable, own PDF / case-file copy)
 *   - LinkedIn profile text (headline, About, skills — copy buttons)
 *   - Growth suggestions: what would open more doors (adviser verifies with the official body)
 *
 * Backend: /resumes/case/:threadId/{translations/fr, linkedin, growth}
 */
import React, { useEffect, useMemo, useState } from 'react';
import useAuthenticatedAxios from '../../../../auth/AuthenticatedAxios';
import { useToast } from '../../../../Context/ToastContext';
import { useConfirm } from '../../../../Context/ConfirmContext';
import { Row, Field, Label, Hint, Input, TextArea, Btn, Banner, Entry, Iframe, PreviewEmpty } from './resumeStyles';
import {
  Section, ContactEditor, SummaryEditor, SkillsEditor, ExperienceEditor, EducationEditor,
  CertificationsEditor, LanguagesEditor, AdditionalEditor, ChipsInput,
} from './ResumeSectionEditors';
import { errMsg, isTokenError, downloadPdf, usePdfPreview, fmtDateTime } from './resumeUtils';

const TABS = [['french', 'French version'], ['linkedin', 'LinkedIn'], ['growth', 'Open more doors']];
const GROWTH_LABEL = {
  certification: 'Certification', licence: 'Licence', credential_recognition: 'Credential recognition',
  language: 'Language', experience: 'Experience', other: 'Other',
};

export default function ResumeExtras({ threadId, resume, resumeUpdatedAt, onChanged }) {
  const [tab, setTab] = useState('french');
  if (!resume) return <Banner $tone="warn">Save the client's resume first (Resume tab) — these are generated from it.</Banner>;
  return (
    <div>
      <Row style={{ marginBottom: 14 }}>
        {TABS.map(([k, l]) => (
          <Btn key={k} $sm $variant={tab === k ? undefined : 'ghost'} onClick={() => setTab(k)}>{l}</Btn>
        ))}
      </Row>
      {tab === 'french' && <FrenchVersion threadId={threadId} french={resume.french} resumeUpdatedAt={resumeUpdatedAt} onChanged={onChanged} />}
      {tab === 'linkedin' && <LinkedIn threadId={threadId} initial={resume.extras?.linkedin} onChanged={onChanged} />}
      {tab === 'growth' && <Growth threadId={threadId} initial={resume.extras?.growth} onChanged={onChanged} />}
    </div>
  );
}

// ---- French ----------------------------------------------------------------

function FrenchVersion({ threadId, french, resumeUpdatedAt, onChanged }) {
  const api = useAuthenticatedAxios();
  const { showToast } = useToast();
  const { confirm } = useConfirm();
  const [content, setContent] = useState(french?.content || null);
  const [saved, setSaved] = useState(JSON.stringify(french?.content || null));
  const [busy, setBusy] = useState('');
  useEffect(() => { setContent(french?.content || null); setSaved(JSON.stringify(french?.content || null)); }, [french]);
  const dirty = JSON.stringify(content || null) !== saved;
  const base = `/resumes/case/${threadId}`;
  const stale = french?.translatedAt && resumeUpdatedAt && new Date(resumeUpdatedAt) > new Date(french.translatedAt);

  const preview = usePdfPreview(
    async () => (await api.post(`${base}/preview?lang=fr`, { content, lang: 'fr' })).data.data.pdfBase64,
    [content], { enabled: !!content }
  );

  const translate = async () => {
    if (content && !(await confirm({ title: 'Translate again?', message: 'The French version is replaced with a fresh translation of the current English resume. Uses AI tokens.', confirmText: 'Translate', type: 'warning' }))) return;
    setBusy('translate');
    try { await api.post(`${base}/translations/fr`); showToast('French version ready — review it', 'success'); onChanged(); }
    catch (e) { if (!isTokenError(e)) showToast(errMsg(e, 'Translation failed'), 'error'); }
    finally { setBusy(''); }
  };
  const save = async () => {
    setBusy('save');
    try { await api.put(`${base}/translations/fr`, { content }); setSaved(JSON.stringify(content)); showToast('French version saved', 'success'); return true; }
    catch (e) { showToast(errMsg(e, 'Could not save'), 'error'); return false; }
    finally { setBusy(''); }
  };
  const ensure = async () => (dirty ? save() : true);
  const remove = async () => {
    if (!(await confirm({ title: 'Delete the French version?', message: 'Copies already saved to case files stay.', confirmText: 'Delete', type: 'danger' }))) return;
    try { await api.delete(`${base}/translations/fr`); onChanged(); } catch (e) { showToast(errMsg(e, 'Could not delete'), 'error'); }
  };

  if (!content) {
    return (
      <Banner>
        A full Canadian-French version of the resume (Québec employers, bilingual roles). Names, employers, dates and numbers are kept exactly.
        <div style={{ marginTop: 10 }}><Btn $variant="ai" onClick={translate} disabled={!!busy}>{busy ? 'Translating…' : '✨ Create the French version'}</Btn></div>
      </Banner>
    );
  }
  const set = (k) => (v) => setContent(c => ({ ...c, [k]: v }));
  const c = { contact: {}, summary: '', skills: [], experience: [], education: [], certifications: [], languages: [], volunteer: [], additional: [], ...content };

  return (
    <>
      {stale && <Banner $tone="warn">The English resume changed after this translation ({fmtDateTime(french.translatedAt)}). Edit below or translate again.</Banner>}
      <Row style={{ alignItems: 'center', marginBottom: 12 }}>
        <Hint style={{ flex: 1 }}>Translated {fmtDateTime(french?.translatedAt)} — edit freely; it is saved separately from the English resume.</Hint>
        <Btn $sm onClick={save} disabled={!dirty || !!busy}>{dirty ? 'Save' : 'Saved'}</Btn>
        <Btn $sm $variant="ghost" onClick={async () => { if (await ensure()) downloadPdf(api, `${base}/download?lang=fr`, `CV - ${c.contact.fullName || 'Client'}.pdf`).catch(() => showToast('Download failed', 'error')); }}>Download PDF</Btn>
        <Btn $sm $variant="green" onClick={async () => {
          if (!(await ensure())) return;
          try { const r = await api.post(`${base}/save-to-files?lang=fr`); showToast(`Saved “${r.data.data.savedFile.originalName}” to case files`, 'success'); }
          catch (e) { showToast(errMsg(e, 'Could not save to case files'), 'error'); }
        }}>Save to case files</Btn>
        <Btn $sm $variant="ghost" onClick={translate} disabled={!!busy}>{busy === 'translate' ? 'Translating…' : 'Translate again'}</Btn>
        <Btn $sm $variant="ghost" style={{ color: '#fca5a5' }} onClick={remove}>Delete</Btn>
      </Row>
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)', gap: 16, alignItems: 'start' }}>
        <div>
          <Section title="Coordonnées et titre"><ContactEditor contact={c.contact} onChange={set('contact')} /></Section>
          <Section title="Profil professionnel"><SummaryEditor value={c.summary} onChange={set('summary')} /></Section>
          <Section title="Compétences"><SkillsEditor groups={c.skills} onChange={set('skills')} /></Section>
          <Section title="Expérience professionnelle" count={c.experience.length}><ExperienceEditor items={c.experience} onChange={set('experience')} /></Section>
          <Section title="Formation" count={c.education.length} defaultOpen={false}><EducationEditor items={c.education} onChange={set('education')} /></Section>
          <Section title="Certifications et permis" count={c.certifications.length} defaultOpen={false}><CertificationsEditor items={c.certifications} onChange={set('certifications')} /></Section>
          <Section title="Langues" count={c.languages.length} defaultOpen={false}><LanguagesEditor items={c.languages} onChange={set('languages')} /></Section>
          <Section title="Bénévolat" count={c.volunteer.length} defaultOpen={false}><ExperienceEditor volunteer items={c.volunteer} onChange={set('volunteer')} /></Section>
          <Section title="Autres sections" count={c.additional.length} defaultOpen={false}><AdditionalEditor items={c.additional} onChange={set('additional')} /></Section>
        </div>
        <div style={{ position: 'sticky', top: 0, height: '78vh', display: 'flex', flexDirection: 'column', gap: 8 }}>
          <Row style={{ alignItems: 'center' }}><Label>Aperçu</Label>{preview.loading && <Hint>rendering…</Hint>}<span style={{ flex: 1 }} /><Btn $sm $variant="ghost" onClick={preview.refresh}>Refresh</Btn></Row>
          {preview.url ? <Iframe title="French preview" src={preview.url} /> : <PreviewEmpty>{preview.error || 'Rendering…'}</PreviewEmpty>}
        </div>
      </div>
    </>
  );
}

// ---- LinkedIn ----------------------------------------------------------------

function CopyBtn({ text }) {
  const { showToast } = useToast();
  return <Btn $sm $variant="ghost" onClick={async () => {
    try { await navigator.clipboard.writeText(text || ''); showToast('Copied', 'success'); } catch { showToast('Copy failed', 'error'); }
  }}>Copy</Btn>;
}

function LinkedIn({ threadId, initial, onChanged }) {
  const api = useAuthenticatedAxios();
  const { showToast } = useToast();
  const [v, setV] = useState(initial || null);
  const [saved, setSaved] = useState(JSON.stringify(initial || null));
  const [busy, setBusy] = useState('');
  const [lang, setLang] = useState('en');
  useEffect(() => { setV(initial || null); setSaved(JSON.stringify(initial || null)); }, [initial]);
  const dirty = JSON.stringify(v || null) !== saved;
  const base = `/resumes/case/${threadId}/linkedin`;

  const generate = async () => {
    setBusy('gen');
    try { const r = await api.post(base, { lang }); setV(r.data.data); setSaved(JSON.stringify(r.data.data)); onChanged(); showToast('LinkedIn profile ready', 'success'); }
    catch (e) { if (!isTokenError(e)) showToast(errMsg(e, 'Generation failed'), 'error'); }
    finally { setBusy(''); }
  };
  const save = async () => {
    setBusy('save');
    try { await api.put(base, v); setSaved(JSON.stringify(v)); showToast('Saved', 'success'); }
    catch (e) { showToast(errMsg(e, 'Could not save'), 'error'); }
    finally { setBusy(''); }
  };
  const aboutLen = useMemo(() => (v?.about || '').length, [v]);

  return (
    <>
      <Row style={{ alignItems: 'center', marginBottom: 12 }}>
        <Hint style={{ flex: 1 }}>Headline, About and skills for the client's LinkedIn profile, from the saved resume. Copy each into LinkedIn.</Hint>
        <select value={lang} onChange={e => setLang(e.target.value)} style={{ background: '#0a0e16', color: '#eef1f7', border: '1px solid rgba(120,140,180,0.2)', borderRadius: 8, padding: '6px 8px' }}>
          <option value="en">English</option><option value="fr">Français</option>
        </select>
        <Btn $sm $variant="ai" onClick={generate} disabled={!!busy}>{busy === 'gen' ? 'Writing…' : v ? 'Re-generate' : '✨ Write LinkedIn profile'}</Btn>
        {v && <Btn $sm onClick={save} disabled={!dirty || !!busy}>{dirty ? 'Save' : 'Saved'}</Btn>}
      </Row>
      {v && (
        <>
          <Entry>
            <Row style={{ alignItems: 'center' }}><Label style={{ flex: 1 }}>Headline ({(v.headline || '').length}/220)</Label><CopyBtn text={v.headline} /></Row>
            <Input style={{ marginTop: 6 }} value={v.headline || ''} maxLength={220} onChange={e => setV({ ...v, headline: e.target.value })} />
          </Entry>
          <Entry>
            <Row style={{ alignItems: 'center' }}><Label style={{ flex: 1 }}>About ({aboutLen}/2600)</Label><CopyBtn text={v.about} /></Row>
            <TextArea style={{ marginTop: 6 }} $h={220} value={v.about || ''} maxLength={2600} onChange={e => setV({ ...v, about: e.target.value })} />
          </Entry>
          <Entry>
            <Row style={{ alignItems: 'center' }}><Label style={{ flex: 1 }}>Skills</Label><CopyBtn text={(v.skills || []).join(', ')} /></Row>
            <div style={{ marginTop: 6 }}><ChipsInput items={v.skills || []} onChange={skills => setV({ ...v, skills })} /></div>
          </Entry>
        </>
      )}
    </>
  );
}

// ---- Growth ------------------------------------------------------------------

function Growth({ threadId, initial, onChanged }) {
  const api = useAuthenticatedAxios();
  const { showToast } = useToast();
  const [plan, setPlan] = useState(initial || null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setPlan(initial || null); }, [initial]);

  const generate = async () => {
    setBusy(true);
    try { const r = await api.post(`/resumes/case/${threadId}/growth`); setPlan(r.data.data); onChanged(); }
    catch (e) { if (!isTokenError(e)) showToast(errMsg(e, 'Could not generate suggestions'), 'error'); }
    finally { setBusy(false); }
  };

  return (
    <>
      <Row style={{ alignItems: 'center', marginBottom: 12 }}>
        <Hint style={{ flex: 1 }}>Certifications, licences, credential recognition or experience that would open more jobs for this client.
          For you, not the client: <b>confirm requirements, fees and timelines with the official body before advising</b> — they change and differ by province.</Hint>
        <Btn $sm $variant="ai" onClick={generate} disabled={busy}>{busy ? 'Thinking…' : plan ? 'Re-generate' : '✨ Suggest what would open more doors'}</Btn>
      </Row>
      {plan?.generatedAt && <Hint $mb>Generated {fmtDateTime(plan.generatedAt)}</Hint>}
      {(plan?.suggestions || []).map((s, i) => (
        <Entry key={i}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: '#c4b5fd', background: 'rgba(139,92,246,0.15)', padding: '2px 8px', borderRadius: 999 }}>{GROWTH_LABEL[s.type] || 'Other'}</span>
            <b style={{ fontSize: 14 }}>{s.title}</b>
          </div>
          <div style={{ fontSize: 13, color: '#c7cee0', marginTop: 6, lineHeight: 1.5 }}>{s.why}</div>
          <Row $mt>
            {s.effort && <Field><Label>Effort</Label><div style={{ fontSize: 13 }}>{s.effort}</div></Field>}
            {s.verifyWith && <Field><Label>Verify with</Label><div style={{ fontSize: 13 }}>{s.verifyWith}</div></Field>}
          </Row>
        </Entry>
      ))}
    </>
  );
}
